import { BRAND } from '@verlo/shared/lib/brand';
import { useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import QRCode from 'react-qr-code';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import { QrCode, Download, Printer, Users } from 'lucide-react';

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

const QRCodeManager = ({ tables }) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const baseUrl = window.location.origin;

  const getOrderUrl = (tableId) => `${baseUrl}/order/${tableId}`;

  const handlePrint = (table) => {
    const url = getOrderUrl(table.id);
    const svg = renderToStaticMarkup(
      <QRCode value={url} size={256} fgColor={BRAND.verlo} />
    );
    const safeName = escapeHtml(table.name);
    const html = `<!doctype html><html><head><title>QR Code - ${safeName}</title></head>
<body style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;margin:0">
  <h2 style="color:${BRAND.verlo};margin-bottom:8px">${safeName}</h2>
  <p style="color:#666;font-size:14px;margin-bottom:24px">Scan to order</p>
  <div>${svg}</div>
  <p style="color:#999;font-size:11px;margin-top:24px">Yard &amp; Coop</p>
</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(() => iframe.parentNode && iframe.parentNode.removeChild(iframe), 1000);
      }
    };
    iframe.srcdoc = html;
  };

  return (
    <>
      <Card className="border shadow-sm" data-testid="qr-code-manager">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <QrCode size={16} />
            QR Table Ordering
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-ink-soft mb-3">Generate QR codes for guest self-service ordering</p>
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {tables.map(table => (
              <div 
                key={table.id}
                className="flex items-center justify-between p-2 border rounded hover:bg-surface-2 transition-colors"
                data-testid={`qr-table-row-${table.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{table.name}</span>
                  <Badge variant="outline" className="text-[10px]">
                    <Users size={8} className="mr-1" />{table.capacity}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setSelectedTable(table)}
                    data-testid={`show-qr-${table.id}`}
                  >
                    <QrCode size={12} className="mr-1" />
                    QR
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-xs"
                    onClick={() => handlePrint(table)}
                    data-testid={`print-qr-${table.id}`}
                  >
                    <Printer size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* QR Code Preview Dialog */}
      <Dialog open={!!selectedTable} onOpenChange={() => setSelectedTable(null)}>
        <DialogContent size="confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode size={18} />
              {selectedTable?.name} QR Code
            </DialogTitle>
            <DialogDescription>
              Guests scan this code to order from their phone
            </DialogDescription>
          </DialogHeader>
          {selectedTable && (
            <div className="flex flex-col items-center py-4">
              <div className="bg-white p-4 rounded-lg border shadow-sm">
                <QRCode
                  value={getOrderUrl(selectedTable.id)}
                  size={200}
                  fgColor={BRAND.verlo}
                  data-testid="qr-code-display"
                />
              </div>
              <p className="text-xs text-ink-soft mt-3 text-center break-all max-w-[240px]">
                {getOrderUrl(selectedTable.id)}
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline" size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(getOrderUrl(selectedTable.id));
                  }}
                >
                  Copy Link
                </Button>
                <Button
                  size="sm"
                  className="bg-verlo hover:bg-verlo-hover"
                  onClick={() => handlePrint(selectedTable)}
                >
                  <Printer size={14} className="mr-1" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QRCodeManager;
